/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button, Tooltip } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadMCNReport } from "../../../../utils/downloadUtils";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { BOOKING_TYPE_OPTIONS, DOCUMENT_TYPE_OPTIONS, STATUS_OPTIONS } from "../../../../constants/reportConstants";
import Link from "antd/lib/typography/Link";
import { normalizeString } from "../../../../utils";

const MCNReport = () => {
    const { createNewRequest, isLatestRequest } = useRequestCancellation();
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(7, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedOriginHubs, setSelectedOriginHubs] = useState([]);
    const [selectedDestinationHubs, setSelectedDestinationHubs] = useState([]);
    const [selectedCurrentHubs, setSelectedCurrentHubs] = useState([]);
    const [selectedCurrentStatuses, setSelectedCurrentStatuses] = useState([]);
    const [selectedBookingType, setSelectedBookingType] = useState();
    const [selectedDoxNondox, setSelectedDoxNondox] = useState();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);

    // Using standard constants from reportConstants.js

    // Fetch hub list on mount
    useEffect(() => {
        const initializeData = async () => {
            try {
                // Get user information
                const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
                const userId = JSON.parse(localStorage.getItem("userId") || "{}");
                setHubUser(user?.roles?.[0]);

                // Fetch hub list
                const hubs = await getHubList();
                setHubList(hubs);
            } catch (error) {
                console.error("Error initializing data:", error);
            }
        };

        initializeData();
    }, []);

    // Set default hub for HUB_ADMIN users
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo") || "{}");
        const userId = JSON.parse(localStorage.getItem("userId") || "{}");

        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;

        if (hubList.length > 0) {
            if (activeHubId) {
                const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
                if (defaultHub) {
                    setSelectedOriginHubs([defaultHub.value]);
                }
            } else if ((user?.roles?.[0] === "ROLE_HUBADMIN" || user?.roles?.[0] === "ROLE_HUBOPERATOR")) {
                const defaultHub = hubList.find(hub => String(hub.value) === String(userId?.userInfo?.premiseId));
                if (defaultHub) {
                    setSelectedOriginHubs([defaultHub.value]);
                }
            }
        }
    }, [hubList]);

    // Fetch report data when filters change
    useEffect(() => {
        // Wait for hubUser to be initialized
        if (!hubUser) {
            return;
        }

        // For HUB_ADMIN users, hub selection is mandatory
        if ((hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR") && selectedOriginHubs.length === 0) {
            return; // Don't fetch if HUB_ADMIN has no hub selected
        }

        const timer = setTimeout(() => {
            fetchReportData();
        }, 300);

        return () => clearTimeout(timer);
    }, [hubUser, dateRange, selectedOriginHubs, selectedDestinationHubs, selectedCurrentHubs, selectedCurrentStatuses, selectedBookingType, selectedDoxNondox, pagination.current, pagination.pageSize]);

    const fetchReportData = async () => {
        setReportData([]);

        // Early return if date range is not set
        if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
            setReportData([]);
            setPagination(prev => ({ ...prev, total: 0 }));
            return;
        }

        // Create new request with global hook
        const { signal, requestId } = createNewRequest("mcn-report");

        try {
            setLoading(true);
            const params = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                originHubIds: selectedOriginHubs.length > 0 ? selectedOriginHubs.join(",") : undefined,
                destinationHubIds: selectedDestinationHubs.length > 0 ? selectedDestinationHubs.join(",") : undefined,
                currentHubIds: selectedCurrentHubs.length > 0 ? selectedCurrentHubs.join(",") : undefined,
                currentStatuses: selectedCurrentStatuses.length > 0 ? selectedCurrentStatuses.join(",") : undefined,
                bookingType: selectedBookingType || undefined,
                doxNondoxTypes: selectedDoxNondox || undefined,
                page: (pagination.current || 1) - 1,
                size: pagination.pageSize || 10
            };

            const response = await ReportsService.getMCNReport(params, {
                signal
            });

            // Only update if this is the latest request
            if (isLatestRequest(requestId)) {
                setReportData(response.data || []);
                setLastsynced(response?.lastRefreshedAt);
                setNextScheduledRefresh(response?.nextScheduledRefresh);
                setPagination(prev => ({
                    ...prev,
                    total: response.total || 0
                }));
            }

        } catch (error) {
            // Don"t update state if request was cancelled
            if (error.name !== "AbortError") {
                console.error("Error fetching MCN report:", error);
                message.error("Failed to fetch MCN report data");
            }
        } finally {
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    const handleDownload = async () => {
        if (!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]) {
            message.warning("Please select date range before downloading");
            return;
        }

        setDownloadLoading(true);
        try {
            const params = {
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                originHubIds: selectedOriginHubs.length > 0 ? selectedOriginHubs : undefined,
                destinationHubIds: selectedDestinationHubs.length > 0 ? selectedDestinationHubs : undefined,
                currentHubIds: selectedCurrentHubs.length > 0 ? selectedCurrentHubs : undefined,
                currentStatuses: selectedCurrentStatuses.length > 0 ? selectedCurrentStatuses : undefined,
                bookingType: selectedBookingType || undefined,
                doxNondoxTypes: selectedDoxNondox || undefined
            };

            await downloadMCNReport(params);
            message.success("MCN report download initiated successfully");
        } catch (error) {
            console.error("Error downloading MCN report:", error);
            message.error("Failed to download MCN report");
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleTableChange = (paginationInfo) => {
        setPagination(prev => ({
            ...prev,
            current: paginationInfo.current,
            pageSize: paginationInfo.pageSize
        }));
    };

    const columns = [
        {
            title: "AWB Number",
            dataIndex: "awbNumber",
            key: "awbNumber",
            width: 150,
            fixed: "left",
            render: (text) => (
                <Link href={`/shipment-tracker?awb=${text}`} target="_blank">
                    {text}
                </Link>
            )
        },
        {
            title: "TPL AWB Number",
            dataIndex: "tplAwbNumber",
            key: "tplAwbNumber",
            width: 150,
            render: (text, rec) => (
                rec?.partnerCode === "DELHIVERY" ?
                    <span style={{
                        color: "#1890ff",
                        cursor: "pointer",

                    }} onClick={() => {
                        window.open(`https://www.delhivery.com/track-v2/package/${text}`, "_blank");
                    }}>
                        {text ? normalizeString(text) : "----"}
                    </span> :
                    <span>{text ? normalizeString(text) : "----"}</span>
            ),

        },
        {
            title: "Partner Code",
            dataIndex: "partnerCode",
            key: "partnerCode",
            width: 120,
            render: (text) => text || "-"
        },
        {
            title: "TPL Transporter ID",
            dataIndex: "tplTransporterId",
            key: "tplTransporterId",
            width: 150,
            render: (text) => text || "-"
        },
        {
            title: "Booking Complete Time",
            dataIndex: "bookingCompleteTime",
            key: "bookingCompleteTime",
            width: 180
        },
        //currentOperationDatetime
        {
            title: "Current Operation Datetime",
            dataIndex: "currentOperationDatetime",
            key: "currentOperationDatetime",
            width: 180
        },
        {
            title: "Current Status",
            dataIndex: "currentStatus",
            key: "currentStatus",
            width: 120
        },
        {
            title: "Booking Type",
            dataIndex: "bookingType",
            key: "bookingType",
            width: 120
        },
        {
            title: "Service Type",
            dataIndex: "serviceType",
            key: "serviceType",
            width: 120
        },
        {
            title: "Travel By",
            dataIndex: "travelBy",
            key: "travelBy",
            width: 100
        },
        {
            title: "Type",
            dataIndex: "doxNondox",
            key: "doxNondox",
            width: 120
        },
        {
            title: "Origin Pincode",
            dataIndex: "originPincode",
            key: "originPincode",
            width: 120
        },
        {
            title: "Origin CP Name",
            dataIndex: "originCpName",
            key: "originCpName",
            width: 150,
            render: (text) => text || "-"
        },
        {
            title: "Origin Hub Name",
            dataIndex: "originHubName",
            key: "originHubName",
            width: 200
        },
        //CURRENT hub name
        {
            title: "Current Hub Name",
            dataIndex: "currentHubName",
            key: "currentHubName",
            width: 200
        },
        {
            title: "Destination Pincode",
            dataIndex: "destinationPincode",
            key: "destinationPincode",
            width: 150
        },
        {
            title: "Destination Hub Name",
            dataIndex: "destinationHubName",
            key: "destinationHubName",
            width: 200
        },
        {
            title: "Receiver Address",
            dataIndex: "receiverAddress",
            key: "receiverAddress",
            width: 200
        }
    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading title="MCN Report" />
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Date Range</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Origin Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Origin Hubs"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedOriginHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedOriginHubs}
                            mode={hubUser !== "ROLE_HUBADMIN" ? "multiple" : "default"}
                            allowClear
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={hubUser === "ROLE_HUBADMIN"}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Destination Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Destination Hubs"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedDestinationHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedDestinationHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Current Hub</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Current Hubs"
                            options={hubList}
                            onChange={(value) => {
                                setSelectedCurrentHubs(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedCurrentHubs}
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Current Status</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Current Statuses"
                            options={STATUS_OPTIONS}
                            onChange={(value) => {
                                setSelectedCurrentStatuses(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedCurrentStatuses}
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Booking Type</div>
                        <DropDownComponent
                            className="small-dropdown"
                            placeholder="Select Booking Type"
                            options={BOOKING_TYPE_OPTIONS}
                            onChange={(value) => {
                                setSelectedBookingType(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedBookingType}
                            allowClear
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Type</div>
                        <DropDownComponent
                            placeholder="Select Document Type"
                            options={DOCUMENT_TYPE_OPTIONS}
                            onChange={(value) => {
                                setSelectedDoxNondox(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedDoxNondox}
                            allowClear
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">&nbsp;</div>
                        <Tooltip title="Download MCN Report">
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={handleDownload}
                                loading={downloadLoading}
                                disabled={!dateRange || dateRange.length !== 2 || !dateRange[0] || !dateRange[1]}
                            >
                                Download
                            </Button>
                        </Tooltip>
                    </Col>
                </Row>

                <div className="table-section">
                    <TableComponent
                        columns={columns}
                        dataSource={reportData}
                        loading={loading}
                        pagination={pagination}
                        onChange={handleTableChange}
                        scroll={{ x: 2000 }}
                        rowKey="awbNumber"
                    />
                </div>
            </Card>
        </div>
    );
};

export default MCNReport;
